/**
 * "An empty result and a failed read must never look the same."
 *
 * The governing rule of spec 087, extracted so it stops being rewritten. A surface
 * that cannot tell *nothing matched* from *the read failed* will always report the
 * reassuring one — and in a financial application the reassuring one is a lie: an A/R
 * report showing zero rows reads as "nothing owed".
 *
 * ## Why this file exists
 *
 * This is the same defect, found for the sixth time:
 *
 *   CC-49595  group sharing — survived two QA rounds behind an empty list
 *   CC-50153  the catalog resolver failing as 200-with-no-tabs (server half)
 *   CC-50321  the report catalog rendering "No reports match your search" on a
 *             failed read (client half) — spec 087
 *   CC-50319  the schedules page shipping untested on the same shape
 *   CC-53047  a failed job-list load rendering byte-identically to an empty queue
 *   CC-53052  `?.data?.rows || []` on the specialized/accounting report reads
 *
 * CC-50321 fixed it correctly but **inline and unexported**, inside
 * `reportService.list`. So the next two instances could only copy it, and spec 087
 * §5 is explicit that a second copy of a safety check is exactly what this repo must
 * not create — a later fix lands on one copy and silently misses the others. That has
 * already happened here twice (two CB pickers, two CSV header heuristics).
 *
 * ## What it does NOT do
 *
 * It does not treat emptiness as failure. `[]` passes through untouched, every time.
 * A firm with no reports, a user with no queued runs, an A/R report with genuinely no
 * rows — all real answers. The *only* thing being rejected is a body whose SHAPE says
 * we could not read it.
 *
 * It also does not catch transport failures: a 503/504/403 or an aborted request
 * already rejects at the axios layer. Those need a caller that stops swallowing the
 * rejection — validating the shape does nothing for them. Both halves are required,
 * which is why CC-53047 had five failure modes across two mechanisms.
 */

/**
 * Assert that `value` is an array, or throw with a message the UI can show verbatim.
 *
 * The message is deliberately owned HERE rather than by the page: the service is what
 * knows the read failed and why, and every consumer renders `error.message`. It always
 * contains "this is not an empty …" because the whole point is to deny the reassuring
 * reading, and it always tells the user what to do next.
 *
 * The three nouns are separate on purpose. The existing catalog copy reads "The report
 * catalog came back … This is not an empty catalog … a malformed category", and that
 * exact wording is pinned by tests because it is what the user reads. A refactor must
 * not reword a user-facing message as a side effect, so the caller supplies each part
 * rather than the helper inflecting one noun into three.
 *
 * @param {*} value              what the endpoint actually returned
 * @param {object} opts
 * @param {string} opts.noun     what was read, as the subject: 'report catalog', 'queue'
 * @param {string} [opts.emptyNoun]  the noun in "not an empty ___". Defaults to `noun`.
 * @param {string} [opts.entryNoun]  the noun in "a malformed ___". Defaults to 'entry'.
 * @param {string} [opts.childKey] when present, every element must be a non-null object
 *                                 carrying this key as an array. Use it whenever a
 *                                 consumer dereferences that child inside a render —
 *                                 a malformed element then throws mid-render instead
 *                                 of surfacing as an error state.
 * @returns {Array} the same array, unchanged
 */
export function requireArray(value, { noun, emptyNoun, entryNoun = 'entry', childKey } = {}) {
  const empty = emptyNoun || noun
  if (!Array.isArray(value)) {
    throw new Error(
      `The ${noun} came back in a shape we couldn't read. ` +
      `This is not an empty ${empty} — try again, and report it if it persists.`
    )
  }
  if (childKey && !value.every((el) => el && typeof el === 'object' && Array.isArray(el[childKey]))) {
    throw new Error(
      `The ${noun} came back with a malformed ${entryNoun}. ` +
      `This is not an empty ${empty} — try again, and report it if it persists.`
    )
  }
  return value
}

/**
 * The same guard for a response ENVELOPE — `{ data: { <key>: [...] } }` — which is the
 * shape every `/reports/*` endpoint uses.
 *
 * Exists because `data?.data?.rows || []` is the exact expression this whole family of
 * bugs is made of: three optional links and a fallback, so a wrong-shaped 200, a
 * missing envelope and a genuine empty list all collapse to the same `[]`. Reaching for
 * this helper instead makes the failure loud and leaves the empty case alone.
 *
 * @param {*} payload          the full axios response body
 * @param {string} key         the array's key inside `payload.data`
 * @param {object} opts        as `requireArray`
 */
export function requireArrayIn(payload, key, opts = {}) {
  return requireArray(payload?.data?.[key], opts)
}
